import { useState, useMemo } from "react";
import dayjs from "dayjs";
import { Plus, Loader2, CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IlamyCalendar } from "@ilamy/calendar";
import type { CalendarEvent } from "@ilamy/calendar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import { convertToIlamyEvent } from "@/types/calendar";
import type { CalendarEventRow } from "@/types/calendar";
import { EventFormDialog } from "./components/calendar/EventFormDialog";
import { DeleteEventDialog } from "./components/calendar/DeleteEventDialog";

import seedData from "../../../seed-data.json";

const pageConfig = (
  seedData.dashboard.pages as Record<string, { title?: string; description?: string }>
)["calendar"];

export function CalendarPage() {
  const { t } = useTranslation("dashboard");
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDbEvent, setSelectedDbEvent] = useState<CalendarEventRow | null>(null);
  const [createDefaults, setCreateDefaults] = useState<{ startTime?: string; endTime?: string }>(
    {},
  );

  // Compute query range: current date +/- 2 months buffer
  const currentMonth = currentDate.format("YYYY-MM");
  const queryRange = useMemo(() => {
    const base = dayjs(currentMonth, "YYYY-MM");
    const start = base.subtract(2, "month").startOf("month").toISOString();
    const end = base.add(2, "month").endOf("month").toISOString();
    return { start, end };
  }, [currentMonth]);

  const { data: dbEvents = [], isLoading } = trpc.calendar.listByRange.useQuery(queryRange);
  const utils = trpc.useUtils();

  const calendarEvents = useMemo(
    () => dbEvents.map((e: CalendarEventRow) => convertToIlamyEvent(e)),
    [dbEvents],
  );

  const deleteMutation = trpc.calendar.delete.useMutation({
    onSuccess: () => {
      utils.calendar.listByRange.invalidate();
      setDeleteDialogOpen(false);
      setSelectedDbEvent(null);
    },
  });

  const updateMutation = trpc.calendar.update.useMutation({
    onSuccess: () => {
      utils.calendar.listByRange.invalidate();
    },
  });

  const handleEventClick = (event: CalendarEvent) => {
    const dbEvent = event.data?.dbEvent as CalendarEventRow | undefined;
    if (dbEvent) {
      setSelectedDbEvent(dbEvent);
      setEditDialogOpen(true);
    }
  };

  const handleCellClick = (info: { start: dayjs.Dayjs; end: dayjs.Dayjs }) => {
    setCreateDefaults({
      startTime: info.start.toISOString(),
      endTime: info.end.toISOString(),
    });
    setCreateDialogOpen(true);
  };

  const handleEventUpdate = (event: CalendarEvent) => {
    const dbEvent = event.data?.dbEvent as CalendarEventRow | undefined;
    if (dbEvent) {
      updateMutation.mutate({
        id: dbEvent.id,
        startTime: event.start.toISOString(),
        endTime: event.end.toISOString(),
      });
    }
  };

  const handleEventAdd = (event: CalendarEvent) => {
    setCreateDefaults({
      startTime: event.start.toISOString(),
      endTime: event.end.toISOString(),
    });
    setCreateDialogOpen(true);
  };

  const handleDateChange = (date: dayjs.Dayjs) => {
    setCurrentDate(date);
  };

  const handleDeleteFromEdit = () => {
    setEditDialogOpen(false);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDbEvent) {
      deleteMutation.mutate({ id: selectedDbEvent.id });
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {pageConfig?.title ?? t("pages.calendar.title", "Calendar")}
          </h1>
          <p className="text-base text-muted-foreground md:text-lg">
            {pageConfig?.description ??
              t("pages.calendar.subtitle", "Schedule and manage your events")}
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateDefaults({});
            setCreateDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          New Event
        </Button>
      </div>

      <Separator />

      {/* Calendar */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <IlamyCalendar
            events={calendarEvents}
            initialView="month"
            initialDate={currentDate}
            onEventClick={handleEventClick}
            onCellClick={handleCellClick}
            onEventUpdate={handleEventUpdate}
            onEventAdd={handleEventAdd}
            onDateChange={handleDateChange}
            timeFormat="24-hour"
          />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && dbEvents.length === 0 && (
        <div className="flex h-32 flex-col items-center justify-center text-center">
          <CalendarDays className="mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="text-muted-foreground">No events yet</p>
          <p className="mt-1 text-sm text-muted-foreground/60">
            Click on the calendar or "New Event" to get started
          </p>
        </div>
      )}

      {/* Create Dialog */}
      <EventFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        mode="create"
        defaultValues={createDefaults}
      />

      {/* Edit Dialog */}
      <EventFormDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setSelectedDbEvent(null);
        }}
        mode="edit"
        defaultValues={selectedDbEvent ? { dbEvent: selectedDbEvent } : undefined}
        onDelete={handleDeleteFromEdit}
      />

      {/* Delete Confirmation */}
      <DeleteEventDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        eventTitle={selectedDbEvent?.title ?? ""}
        isRecurring={!!selectedDbEvent?.recurrence_rule}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}

export default CalendarPage;
